import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save } from "lucide-react";
import { Button, FormField, Modal, SelectInput, TextInput } from "../ui";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import {
  fornecedorService,
  produtoService,
  unidadeControleService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";

const fornecedorVazio = {
  razaoSocial: "",
  nomeFantasia: "",
  cpfCnpj: "",
  contato: "",
  telefone: "",
  email: "",
  observacoes: "",
};

const unidadeVazia = {
  nome: "",
  simbolo: "",
};

const produtoVazio = {
  descricao: "",
  unidadeControleId: "",
  fornecedorId: "",
  codigo: "",
};

const digitos = (valor) => String(valor || "").replace(/\D/g, "");

export default function CadastroRapidoModal({
  open,
  tipo,
  onClose,
  onCreated,
  produtoInicial = {},
}) {
  const client = useQueryClient();
  const { showError, showSuccess } = useNotification();
  const confirmar = useConfirmDialog();
  const [verificando, setVerificando] = useState(false);
  const [tipoAtual, setTipoAtual] = useState(tipo);
  const [retornoProduto, setRetornoProduto] = useState(null);
  const [fornecedor, setFornecedor] = useState(fornecedorVazio);
  const [unidade, setUnidade] = useState(unidadeVazia);
  const [produto, setProduto] = useState(produtoVazio);
  const {
    descricao: descricaoInicial = "",
    unidadeControleId: unidadeInicial = "",
    unidadeSimbolo: unidadeSimboloInicial = "",
    fornecedorId: fornecedorInicial = "",
    codigo: codigoInicial = "",
  } = produtoInicial;

  const unidades = useQuery({
    queryKey: ["cadastro-rapido", "unidades"],
    queryFn: () => unidadeControleService.listar({ situacao: "ATIVO" }),
    enabled: open && tipoAtual === "produto",
    staleTime: 30000,
    retry: 0,
  });
  const fornecedores = useQuery({
    queryKey: ["cadastro-rapido", "fornecedores"],
    queryFn: () => fornecedorService.listar({ situacao: "ATIVO" }),
    enabled: open && tipoAtual === "produto",
    staleTime: 30000,
    retry: 0,
  });
  const similaridade = useQuery({
    queryKey: [
      "cadastro-rapido",
      "similaridade",
      produto.descricao.trim(),
      produto.unidadeControleId,
      produtoInicial.ean || "",
    ],
    queryFn: () =>
      produtoService.similaridade({
        descricao: produto.descricao.trim(),
        unidadeControleId: produto.unidadeControleId,
        ean: produtoInicial.ean || undefined,
      }),
    enabled: false,
    retry: 0,
  });

  useEffect(() => {
    if (!open) return;

    setTipoAtual(tipo);
    setRetornoProduto(null);
    setFornecedor(fornecedorVazio);
    setUnidade(unidadeVazia);
    setProduto({
      ...produtoVazio,
      descricao: descricaoInicial,
      unidadeControleId: unidadeInicial,
      fornecedorId: fornecedorInicial,
      codigo: codigoInicial,
    });
  }, [
    open,
    tipo,
    descricaoInicial,
    unidadeInicial,
    unidadeSimboloInicial,
    fornecedorInicial,
    codigoInicial,
  ]);

  useEffect(() => {
    if (
      !open ||
      tipoAtual !== "produto" ||
      produto.unidadeControleId ||
      !unidadeSimboloInicial ||
      !unidades.data?.length
    ) {
      return;
    }

    const simbolo = unidadeSimboloInicial.trim().toLocaleLowerCase("pt-BR");
    const unidadeCorrespondente = unidades.data.find(
      (item) => item.simbolo?.trim().toLocaleLowerCase("pt-BR") === simbolo,
    );
    if (unidadeCorrespondente) {
      setProduto((atual) => ({
        ...atual,
        unidadeControleId: unidadeCorrespondente.id,
      }));
    }
  }, [
    open,
    tipoAtual,
    produto.unidadeControleId,
    unidadeSimboloInicial,
    unidades.data,
  ]);

  const invalidar = () =>
    Promise.all(
      [
        ["produtos"],
        ["produtos-cadastro"],
        ["fornecedores"],
        ["fornecedores-ativos"],
        ["unidades"],
        ["unidades-controle-ativas"],
        ["cadastro-rapido", "fornecedores"],
        ["cadastro-rapido", "unidades"],
        ["pedidos-compra"],
        ["pedidos-entrada"],
        ["saidas-estoque"],
        ["entrada-notas"],
      ].map((queryKey) => client.invalidateQueries({ queryKey })),
    );

  const adicionarAoCache = (queryKey, salvo) => {
    client.setQueryData(queryKey, (atual) => {
      const lista = Array.isArray(atual) ? atual : [];
      if (lista.some((item) => item.id === salvo.id)) return lista;

      return [...lista, salvo].sort((a, b) =>
        String(a.razaoSocial || a.nome || "").localeCompare(
          String(b.razaoSocial || b.nome || ""),
          "pt-BR",
        ),
      );
    });
  };

  const criar = useMutation({
    mutationFn: () => {
      if (tipoAtual === "fornecedor") {
        return fornecedorService.criar({
          ...fornecedor,
          cpfCnpj: digitos(fornecedor.cpfCnpj),
          telefone: digitos(fornecedor.telefone),
        });
      }

      if (tipoAtual === "unidade") {
        return unidadeControleService.criar({
          ...unidade,
          simbolo: unidade.simbolo.trim().toUpperCase(),
        });
      }

      if (produto.fornecedorId && !produto.codigo.trim()) {
        throw new Error(
          "Informe o código no fornecedor antes de salvar o produto.",
        );
      }

      return produtoService.criar({
        descricao: produto.descricao.trim(),
        unidadeControleId: produto.unidadeControleId,
        fornecedores: produto.fornecedorId
          ? [
              {
                fornecedorId: produto.fornecedorId,
                codigo: produto.codigo.trim(),
                principal: true,
              },
            ]
          : [],
      });
    },
    onSuccess: async (salvo, variaveis) => {
      await invalidar();

      if (retornoProduto) {
        if (tipoAtual === "unidade") {
          adicionarAoCache(["cadastro-rapido", "unidades"], salvo);
          adicionarAoCache(["unidades-controle-ativas"], salvo);
          setProduto((atual) => ({ ...atual, unidadeControleId: salvo.id }));
        }

        if (tipoAtual === "fornecedor") {
          adicionarAoCache(["cadastro-rapido", "fornecedores"], salvo);
          adicionarAoCache(["fornecedores-ativos"], salvo);
          setProduto((atual) => ({ ...atual, fornecedorId: salvo.id }));
        }

        setTipoAtual("produto");
        setRetornoProduto(null);
        showSuccess("Cadastro concluído. Continue o produto.");
        return;
      }

      const candidatos = variaveis?.similaridade?.candidatos || [];
      const aviso = candidatos.length
        ? ` Atenção: produto semelhante encontrado: ${candidatos
            .map((item) => item.descricao)
            .join(", ")}.`
        : "";
      showSuccess(`Cadastro concluído com sucesso.${aviso}`);
      if (variaveis?.similaridade?.similar)
        onCreated?.(salvo, tipoAtual, variaveis.similaridade);
      else onCreated?.(salvo, tipoAtual);
      onClose?.();
    },
    onError: (erro) =>
      showError(erro?.message || "Não foi possível concluir o cadastro."),
  });

  const abrirAuxiliar = (novoTipo) => {
    setRetornoProduto(produto);
    setTipoAtual(novoTipo);
  };

  const voltarProduto = () => {
    setProduto(retornoProduto || produto);
    setRetornoProduto(null);
    setTipoAtual("produto");
  };

  const salvar = async (evento) => {
    evento.preventDefault();
    if (verificando || criar.isPending) return;

    let alerta = null;
    if (
      tipoAtual === "produto" &&
      typeof produtoService.similaridade === "function"
    ) {
      setVerificando(true);
      try {
        const resultado = await similaridade.refetch();
        if (resultado.isError) {
          showError(
            resultado.error?.message ||
              "Não foi possível verificar produtos semelhantes. Tente novamente.",
          );
          return;
        }
        alerta = resultado.data || null;
        const candidatos = alerta?.candidatos || [];

        if (
          alerta?.similar &&
          !(await confirmar({
            title: "Produto semelhante encontrado",
            description: `Já existe produto semelhante: ${candidatos
              .map((item) => item.descricao)
              .join(", ")}. Deseja cadastrar mesmo assim?`,
            confirmText: "Cadastrar mesmo assim",
            cancelText: "Cancelar",
            variant: "warning",
          }))
        ) {
          return;
        }
      } finally {
        setVerificando(false);
      }
    }

    criar.mutate({ similaridade: alerta });
  };

  const titulo =
    tipoAtual === "produto"
      ? "Cadastrar produto"
      : tipoAtual === "fornecedor"
        ? "Cadastrar fornecedor"
        : "Cadastrar unidade de controle";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={titulo}
      description={
        tipoAtual === "produto"
          ? "O produto ficará selecionado nesta operação após o cadastro."
          : "Os dados poderão ser complementados depois na tela de cadastros."
      }
      className="max-w-2xl"
    >
      <form onSubmit={salvar} className="space-y-4">
        {tipoAtual === "fornecedor" && (
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Razão social" required>
              <TextInput
                aria-label="Razão social"
                autoFocus
                required
                maxLength={180}
                value={fornecedor.razaoSocial}
                onChange={(evento) =>
                  setFornecedor((atual) => ({
                    ...atual,
                    razaoSocial: evento.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="Nome fantasia">
              <TextInput
                aria-label="Nome fantasia"
                maxLength={180}
                value={fornecedor.nomeFantasia}
                onChange={(evento) =>
                  setFornecedor((atual) => ({
                    ...atual,
                    nomeFantasia: evento.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="CPF/CNPJ">
              <TextInput
                aria-label="CPF/CNPJ"
                inputMode="numeric"
                value={fornecedor.cpfCnpj}
                onChange={(evento) =>
                  setFornecedor((atual) => ({
                    ...atual,
                    cpfCnpj: evento.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="Contato">
              <TextInput
                aria-label="Contato"
                maxLength={160}
                value={fornecedor.contato}
                onChange={(evento) =>
                  setFornecedor((atual) => ({
                    ...atual,
                    contato: evento.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="Telefone">
              <TextInput
                aria-label="Telefone"
                inputMode="tel"
                value={fornecedor.telefone}
                onChange={(evento) =>
                  setFornecedor((atual) => ({
                    ...atual,
                    telefone: evento.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="E-mail">
              <TextInput
                aria-label="E-mail"
                type="email"
                maxLength={320}
                value={fornecedor.email}
                onChange={(evento) =>
                  setFornecedor((atual) => ({
                    ...atual,
                    email: evento.target.value,
                  }))
                }
              />
            </FormField>
          </div>
        )}

        {tipoAtual === "unidade" && (
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Nome" required>
              <TextInput
                aria-label="Nome"
                autoFocus
                required
                maxLength={80}
                value={unidade.nome}
                onChange={(evento) =>
                  setUnidade((atual) => ({
                    ...atual,
                    nome: evento.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="Sigla" required hint="Ex.: UN, CX ou PCT.">
              <TextInput
                aria-label="Sigla"
                required
                maxLength={12}
                value={unidade.simbolo}
                onChange={(evento) =>
                  setUnidade((atual) => ({
                    ...atual,
                    simbolo: evento.target.value.toUpperCase(),
                  }))
                }
              />
            </FormField>
          </div>
        )}

        {tipoAtual === "produto" && (
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Descrição" required className="md:col-span-2">
              <TextInput
                aria-label="Descrição"
                autoFocus
                required
                maxLength={220}
                value={produto.descricao}
                onChange={(evento) =>
                  setProduto((atual) => ({
                    ...atual,
                    descricao: evento.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="Unidade de controle" required>
              <div className="flex gap-2">
                <SelectInput
                  aria-label="Unidade de controle"
                  required
                  value={produto.unidadeControleId}
                  onChange={(evento) =>
                    setProduto((atual) => ({
                      ...atual,
                      unidadeControleId: evento.target.value,
                    }))
                  }
                >
                  <option value="">Selecione</option>
                  {(unidades.data || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nome} ({item.simbolo})
                    </option>
                  ))}
                </SelectInput>
                <Button
                  type="button"
                  variant="secondary"
                  icon={Plus}
                  onClick={() => abrirAuxiliar("unidade")}
                >
                  Unidade
                </Button>
              </div>
            </FormField>
            <FormField label="Fornecedor">
              <div className="flex gap-2">
                <SelectInput
                  aria-label="Fornecedor"
                  value={produto.fornecedorId}
                  onChange={(evento) =>
                    setProduto((atual) => ({
                      ...atual,
                      fornecedorId: evento.target.value,
                    }))
                  }
                >
                  <option value="">Não vincular agora</option>
                  {(fornecedores.data || []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.razaoSocial}
                    </option>
                  ))}
                </SelectInput>
                <Button
                  type="button"
                  variant="secondary"
                  icon={Plus}
                  onClick={() => abrirAuxiliar("fornecedor")}
                >
                  Fornecedor
                </Button>
              </div>
            </FormField>
            {produto.fornecedorId && (
              <FormField
                label="Código no fornecedor *"
                required
                hint="Obrigatório quando houver fornecedor vinculado."
              >
                <TextInput
                  aria-label="Código no fornecedor"
                  required
                  maxLength={160}
                  value={produto.codigo}
                  onInvalid={() =>
                    showError(
                      "Informe o código no fornecedor antes de salvar o produto.",
                    )
                  }
                  onChange={(evento) =>
                    setProduto((atual) => ({
                      ...atual,
                      codigo: evento.target.value,
                    }))
                  }
                />
              </FormField>
            )}
          </div>
        )}

        <footer className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          {retornoProduto && (
            <Button type="button" variant="ghost" onClick={voltarProduto}>
              Voltar ao produto
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="success"
            icon={Save}
            loading={criar.isPending || verificando}
          >
            {tipoAtual === "produto" ? "Salvar produto" : "Salvar"}
          </Button>
        </footer>
      </form>
    </Modal>
  );
}
